import React, { useState, useRef } from 'react';
import { IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonLabel, IonPage, IonRow, IonSelectOption, IonText, IonButton, IonImg } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { checkbox, openOutline } from 'ionicons/icons';
import Toolbar from '../components/Toolbar';
import './Recommendations.scss'
import SelectNoCache from '../components/SelectNoCache';
import { Language, useCountry, useLanguage } from '../utils/Settings';
import { Country } from '../utils/Regions';

import RecommendationsByCountry from '../data/RecommendationsByCountry.json';
import RecommendationsGenericEs from '../data/RecommendationsGenericEs.json';
import RecommendationsGenericEn from '../data/RecommendationsGenericEn.json';
import RecommendationsGenericPr from '../data/RecommendationsGenericPr.json';
import PDFViewer from '../components/PDFViewer';

enum Method {
  AC = "Anti Cálcico",
  ACDiuretic = "Anti Cálcico + diuréticos",
  Diuretic = "Diuréticos",
  IECAARA = "IECA o ARA",
  IECAARAAC = "IECA o ARA + Anti Cálcico",
  IECAARADiuréticos = "IECA o ARA + diuréticos"
};

function getMethodStrForLanguage(method: Method, language: Language) {
  let methodStr = method as string;

  if (language === Language.English) {
    switch (method) {
      case Method.Diuretic:
        methodStr = "Diuretic"; break;
      case Method.AC:
        methodStr = "CCB"; break;
      case Method.ACDiuretic:
        methodStr = "CCB + diuretic"; break;
      case Method.IECAARA:
        methodStr = "ACE or ARB"; break;
      case Method.IECAARAAC:
        methodStr = "ACE-I or ARB + CCB"; break;
      case Method.IECAARADiuréticos:
        methodStr = "ACE-I or ARB + diuretic"; break;
    }
  } else if (language === Language.Portuguese) {
    switch (method) {
      case Method.Diuretic:
        methodStr = "Diurético"; break;
      case Method.AC:
        methodStr = "BCC"; break;
      case Method.ACDiuretic:
        methodStr = "BCC + diurético"; break;
      case Method.IECAARA:
        methodStr = "IECA ou BRA"; break;
      case Method.IECAARAAC:
        methodStr = "IECA ou BRA + BCC"; break;
      case Method.IECAARADiuréticos:
        methodStr = "IECA ou BRA + diurético"; break;
    }
  }

  return methodStr;
}

function getGenericRecommendation(method: Method, language: Language) {
  let recommendations;
  if (language === Language.English)
    recommendations = RecommendationsGenericEn;
  else if (language === Language.Portuguese)
    recommendations = RecommendationsGenericPr;
  else
    recommendations = RecommendationsGenericEs;

  let methodStr = getMethodStrForLanguage(method, language);

  for (let i = 0; i < recommendations.length; i++) {
    let data: { [key: string]: any } = recommendations[i];
    if (data && data["Method"] === methodStr)
      return data;
  }
  return null;
}

function getRecommendationForCountry(country: Country) {
  let countryStr = country as string;
  if (country === Country.DominicanRepublic)
    countryStr = "Dominicana";
  else if (country === Country.SaintLucia)
    countryStr = "Santa Lucia";
  else if (country === Country.TrinidadandTobago)
    countryStr = "Trinidad y Tobago";

  for (let i = 0; i < RecommendationsByCountry.length; i++) {
    let data: { [key: string]: any } = RecommendationsByCountry[i];
    if (data && data["País"] === countryStr)
      return data;
  }
  return null;
}

const Recommendations: React.FC = () => {
  const { t } = useTranslation();
  const [language] = useLanguage();
  const [country] = useCountry();
  const [method, setMethod] = useState<Method>(Method.AC);
  const contentRef = useRef<HTMLIonContentElement | null>(null);

  let showGenericRecommendation = false;
  var data = getRecommendationForCountry(country);
  if (!data) {
    data = getGenericRecommendation(method, language);
    showGenericRecommendation = true;
  }

  if (!data)
    throw new Error("Missing recommendation");

  if (country === Country.Panamá) {
    let pdfUrl = "assets/Panama.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir -  PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }
  if (country === Country.Argentina) {
    let pdfUrl = "assets/Argentina.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }
  if (country === Country.Bolivia) {
    let pdfUrl = "assets/Bolivia.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Chile) {
    let pdfUrl = "assets/Chile.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.ElSalvador) {
    let pdfUrl = "assets/ElSalvador.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }


  if (country === Country.DominicanRepublic) {
    let pdfUrl = "assets/RepDominicana.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Barbados) {
    let pdfUrl = "assets/Barbados.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Anguila) {
    let pdfUrl = "assets/Anguilla.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Grenada) {
    let pdfUrl = "assets/Grenada.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Uruguay) {
    let pdfUrl = "assets/Uruguay.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }
  if (country === Country.Belize) {
    let pdfUrl = "assets/Belize.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

    if (country === Country.Guatemala) {
    let pdfUrl = "assets/Guatemala.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.BritishVirginIslands) {
    let pdfUrl = "assets/BVI.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Cuba) {
    let pdfUrl = "assets/Cuba.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Dominica) {
    let pdfUrl = "assets/Dominica.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Guyana) {
    let pdfUrl = "assets/Guyana.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Perú) {
    let pdfUrl = "assets/Peru.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Ecuador) {
    let pdfUrl = "assets/Ecuador.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Abrir PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.SaintLucia) {
    let pdfUrl = "assets/SaintLucia.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }
  if (country === Country.Suriname) {
    let pdfUrl = "assets/Suriname.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.TrinidadandTobago) {
    let pdfUrl = "assets/TTO.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Bahamas) {
    let pdfUrl = "assets/Bahamas.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Bermuda) {
    let pdfUrl = "assets/Bermuda.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.Brasil) {
  let pdfUrl = "assets/StandardPOR.pdf";

  return (
    <IonPage id="recommmend-page">
      <IonHeader>
        <Toolbar title={t('Recommendations')} />
      </IonHeader>

      <IonContent fullscreen ref={contentRef}>
        <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>

          <IonRow className="ion-justify-content-between">
            <IonCol size="auto">
              <IonLabel>
                <b>{t(country)}</b>
                <br />
                <i>
                  {t("Seu país ainda não aprovou oficialmente uma via clínica. Recomendamos que consulte a proposta de via clínica padrão desenvolvida pela equipe do HEARTS nas Américas.")}
                </i>
              </IonLabel>
            </IonCol>

            <IonCol size="auto">
              <IonButton href={pdfUrl} rel="external" target="_blank">
                {t('Abrir PDF')}
                <IonIcon icon={openOutline} />
              </IonButton>
            </IonCol>
          </IonRow>

          <IonRow style={{ height: "100%" }}>
            <IonCol size="12" style={{ height: "100%" }}>
              <PDFViewer file={pdfUrl} />
            </IonCol>
          </IonRow>

        </IonGrid>
      </IonContent>
    </IonPage>
  );
}

  if (country === Country.AntiguaAndBarbuda ||
    country === Country.Canada ||
    country === Country.EstadosUnidosdeNA ||
    country === Country.Groenlandia ||
    country === Country.Haiti ||
    country === Country.Jamaica ||
    country === Country.Montserrat ||
    country === Country.SaintVincentandtheGrenadines ||
    country === Country.TurksCaicos ||
    country === Country.StKittsNevis) {
    let pdfUrl = "assets/StandardENG.pdf";
    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>
        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>
            <IonRow className="ion-justify-content-between">
              <IonCol size="auto">
                <IonLabel><b>{t(country)}</b><br />
                  <i>{t("Your country has not yet officially approved a clinical pathway. We advise you to consult the proposed standard clinical pathway developed by the HEARTS in the Americas team.")}</i>
                </IonLabel>
              </IonCol>
              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (country === Country.México) {
  let pdfUrl = "assets/PRONAM.pdf";

  return (
    <IonPage id="recommmend-page">
      <IonHeader>
        <Toolbar title={t('Recommendations')} />
      </IonHeader>

      <IonContent fullscreen ref={contentRef}>
        <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>

          <IonRow className="ion-justify-content-between">
            <IonCol size="auto">
              <IonLabel>
                <b>{t(country)}</b>
                <br />
                <i
                  dangerouslySetInnerHTML={{
                    __html: t('mxNoClinicalPathwayNote'),
                  }}
                />
              </IonLabel>
            </IonCol>

            <IonCol size="auto">
              <IonButton href={pdfUrl} rel="external" target="_blank">
                {t('Abrir PDF')}
                <IonIcon icon={openOutline} />
              </IonButton>
            </IonCol>
          </IonRow>

          <IonRow style={{ height: "100%" }}>
            <IonCol size="12" style={{ height: "100%" }}>
              <PDFViewer file={pdfUrl} />
            </IonCol>
          </IonRow>

        </IonGrid>
      </IonContent>
    </IonPage>
  );
}




  if (
    country === Country.Colombia ||
    country === Country.CostaRica ||
    country === Country.Honduras ||
    country === Country.Nicaragua ||
    country === Country.Paraguay ||
    country === Country.PuertoRico ||
    country === Country.Venezuela
  ) {
    let pdfUrl = "assets/StandardESP.pdf";

    return (
      <IonPage id="recommmend-page">
        <IonHeader>
          <Toolbar title={t('Recommendations')} />
        </IonHeader>

        <IonContent fullscreen ref={contentRef}>
          <IonGrid className="dashboard" style={{ height: "calc(100% - 40px)" }}>

            <IonRow className="ion-justify-content-between">

              <IonCol size="auto">
                <IonLabel>
                  <b>{t(country)}</b><br />
                  <i>{t('unapprovedClinicalPathway')}</i>
                </IonLabel>
              </IonCol>

              <IonCol size="auto">
                <IonButton href={pdfUrl} rel="external" target="_blank">
                  {t('Open PDF')}
                  <IonIcon icon={openOutline} />
                </IonButton>
              </IonCol>

            </IonRow>

            <IonRow style={{ height: "100%" }}>
              <IonCol size="12" style={{ height: "100%" }}>
                <PDFViewer file={pdfUrl} />
              </IonCol>
            </IonRow>

          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage id="recommmend-page">
      <IonHeader>
        <Toolbar title={t('Recommendations')} />
      </IonHeader>
      <IonContent fullscreen ref={contentRef} className="ion-padding">
        <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Recommendations')}</b></h1></IonText>
        <div className="dashboard">
          <IonGrid>
            <IonRow>
              <IonCol size="12">
                <IonLabel><b>{t(country)}</b></IonLabel>
              </IonCol>
              {
                showGenericRecommendation &&
                <IonCol size="auto">
                  <IonLabel class="methodLabel"><b>{t('Method')}</b></IonLabel>
                </IonCol>
              }
              {
                showGenericRecommendation &&
                <IonCol size="auto">
                  <SelectNoCache className="formSelect" interface="popover" value={method} onIonChange={e => setMethod(e.detail.value)}>
                    <IonSelectOption key={Method.AC} value={Method.AC}>{getMethodStrForLanguage(Method.AC, language)}</IonSelectOption>
                    <IonSelectOption key={Method.ACDiuretic} value={Method.ACDiuretic}>{getMethodStrForLanguage(Method.ACDiuretic, language)}</IonSelectOption>
                    <IonSelectOption key={Method.Diuretic} value={Method.Diuretic}>{getMethodStrForLanguage(Method.Diuretic, language)}</IonSelectOption>
                    <IonSelectOption key={Method.IECAARA} value={Method.IECAARA}>{getMethodStrForLanguage(Method.IECAARA, language)}</IonSelectOption>
                    <IonSelectOption key={Method.IECAARAAC} value={Method.IECAARAAC}>{getMethodStrForLanguage(Method.IECAARAAC, language)}</IonSelectOption>
                    <IonSelectOption key={Method.IECAARADiuréticos} value={Method.IECAARADiuréticos}>{getMethodStrForLanguage(Method.IECAARADiuréticos, language)}</IonSelectOption>
                  </SelectNoCache>
                </IonCol>
              }
            </IonRow>
            <IonRow>
              <IonCol>
                <IonLabel class="dataHeader">
                  <b dangerouslySetInnerHTML={{ __html: data.Encabezado }} />
                </IonLabel>
              </IonCol>
            </IonRow>
          </IonGrid>

          <div id="main-content">
            <IonGrid class="reco">
              <IonRow>
                <IonCol class="ion-no-padding">
                  <IonLabel className="recoHeader">
                    <b>{data.RecoHeader1 || t('Pharmacological recommendations')}</b>
                  </IonLabel>
                </IonCol>
              </IonRow>
              {
                data.SubHeader &&
                <IonRow>
                  <IonCol>
                    <span>{data.SubHeader}</span>
                  </IonCol>
                </IonRow>
              }
            </IonGrid>
            <div key={0} className="stepItem" hidden={!data || !data.TextoIntroductorio}>
              <IonGrid>
                <IonRow>
                  <IonCol size='12' size-md='5'>
                    <div className="container">
                      <div className="breadcrumb">
                        <b>
                          <span className="breadcrumb__inner">
                            <span className="breadcrumb__step">{data.Step ? data.Step : t('Step')} {1}</span>
                          </span>
                        </b>

                      </div>
                    </div>
                  </IonCol>
                  <IonCol size='12' size-md='7' className="backGround">
                    <IonText>
                      <b>{data && data.TextoIntroductorio}</b>
                    </IonText>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </div>

            {
              (!data?.TextoIntroductorio
                ? ['1', '2', '3', '4', '5', '6', '7']
                : ['2', '3', '4', '5', '6', '7'])
                .map((value: string, index: number) =>
                  data && data[`Criterio${index + 1}`] &&
                  <div key={index} className="stepItem">

                    <IonGrid>
                      <IonRow>
                        <IonCol size='12' size-md='5'>
                          <div className="container">
                            <div className="breadcrumb">
                              <b>
                                <span className="breadcrumb__inner">
                                  <span className="breadcrumb__step">{data.Step ? data.Step : t('Step')} {value}</span>
                                </span>
                              </b>
                              {data && index > 0 && data[`Tiempo${index}`] &&
                                <b>

                                  <span className="breadcrumb__inner">
                                    <span className="breadcrumb__time">
                                      {data && index > 0 && data[`Tiempo${index}`]
                                      }
                                    </span>
                                  </span>
                                </b>
                              }
                            </div>
                          </div>
                        </IonCol>
                        <IonCol size='12' size-md='7' className="backGround">
                          <IonText>
                            <b dangerouslySetInnerHTML={{ __html: data && data[`Criterio${index + 1}`] }} />
                            <br />
                            <div dangerouslySetInnerHTML={{ __html: data && data[`Conducta${index + 1}`].replace(/\n/g, '<br/>') }} />
                          </IonText>
                        </IonCol>
                      </IonRow>
                    </IonGrid>

                  </div>
                )
            }

            {data.Posterior &&
              <div key={10} className="stepItem">
                <IonGrid>
                  <IonRow>
                    <IonCol size='12' size-md='5'>
                    </IonCol>
                    <IonCol size='12' size-md='7'>
                      <div dangerouslySetInnerHTML={{ __html: data.Posterior.replace('\n', '<br/>') }} />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </div>
            }

            {
              showGenericRecommendation &&
              <>
                <h2>{t('MedicationsAndDosage')}</h2>
                <table className="MedicationsTable">
                  <thead>
                    <tr>
                      <th>{t('Class')}</th>
                      <th>{t('Medication')}</th>
                      <th>{t('InitialDose')}</th>
                      <th>{t('DoseAugmented')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td rowSpan={2} dangerouslySetInnerHTML={{ __html: t('Diuretic') }} />
                      <td rowSpan={2} dangerouslySetInnerHTML={{ __html: t('DiureticMedication') }} />
                      <td>12,5 mg</td>
                      <td>25 mg</td>
                    </tr>
                    <tr>
                      <td>1,5 mg</td>
                      <td>{t('StayAt')} 1,5 mg</td>
                    </tr>
                    <tr>
                      <td rowSpan={3} dangerouslySetInnerHTML={{ __html: t('IECA') }} />
                      <td>{t('Lisinopril')}</td>
                      <td>20 mg</td>
                      <td>40 mg</td>
                    </tr>
                    <tr>
                      <td>{t('Ramipril')}</td>
                      <td>5 mg</td>
                      <td>10 mg</td>
                    </tr>
                    <tr>
                      <td>{t('Perindopril')}</td>
                      <td>4-5 mg</td>
                      <td>8-10 mg</td>
                    </tr>
                    <tr>
                      <td rowSpan={2} dangerouslySetInnerHTML={{ __html: t('ARB') }} />
                      <td>{t('Losartan')}</td>
                      <td>50 mg</td>
                      <td>100 mg</td>
                    </tr>
                    <tr>
                      <td>{t('Telmisartan')}</td>
                      <td>40 mg</td>
                      <td>80 mg</td>
                    </tr>
                    <tr>
                      <td dangerouslySetInnerHTML={{ __html: t('AC') }} />
                      <td>{t('Amlodipine')}</td>
                      <td>5 mg</td>
                      <td>10 mg</td>
                    </tr>
                  </tbody>
                </table>
              </>
            }

            <IonGrid class="reco">
              {
                data.RecoHeader &&
                <IonRow>
                  <IonCol class="ion-no-padding">
                    <IonLabel className="recoHeader">
                      <b>{data.RecoHeader2 || t('Non-pharmacological recommendations')}</b>
                    </IonLabel>
                  </IonCol>
                </IonRow>
              }
              {
                data.RecoHeader &&
                <IonRow className="recoContent">
                  <IonCol size="6" size-md="2.4" className="ion-text-center">
                    <IonImg className="recommendationIcon" src="assets/recommendations/Smoke.png" />
                    <b><p dangerouslySetInnerHTML={{ __html: data.RecoSmoke }} /></b>
                  </IonCol>
                  <IonCol size="6" size-md="2.4" className="ion-text-center">
                    <IonImg className="recommendationIcon" src="assets/recommendations/Alcohol.png" />
                    <b><p dangerouslySetInnerHTML={{ __html: data.RecoAlcohol }} /></b>
                  </IonCol>
                  <IonCol size="6" size-md="2.4" className="ion-text-center">
                    <IonImg className="recommendationIcon" src="assets/recommendations/Exercise.png" />
                    <b><p dangerouslySetInnerHTML={{ __html: data.RecoExcercise }} /></b>
                  </IonCol>
                  <IonCol size="6" size-md="2.4" className="ion-text-center">
                    <IonImg className="recommendationIcon" src="assets/recommendations/Overweight.png" />
                    <b><p dangerouslySetInnerHTML={{ __html: data.RecoWeight }} /></b>
                  </IonCol>
                  <IonCol size="12" size-md="2.4" className="ion-text-center">
                    <IonImg className="recommendationIcon" src="assets/recommendations/Diet.png" />
                    <b><p dangerouslySetInnerHTML={{ __html: data.RecoGeneral1 }}></p></b>
                  </IonCol>
                  <hr />
                  {
                    data.RecoGeneral2 &&
                    <IonCol size="12" size-sm="4">
                      <IonRow>
                        <IonCol>
                          <IonIcon icon={checkbox} color="primary"></IonIcon>
                          &nbsp;<span>{data.RecoGeneral2}</span>
                        </IonCol>
                      </IonRow>
                      {data.RecoGeneral5 &&
                        <IonRow>
                          <IonCol>
                            <IonIcon icon={checkbox} color="primary"></IonIcon>
                            &nbsp;<span>{data.RecoGeneral5}</span>
                          </IonCol>
                        </IonRow>
                      }
                      {data.RecoGeneral8 &&
                        <IonRow>
                          <IonCol>
                            <IonIcon icon={checkbox} color="primary"></IonIcon>
                            &nbsp;<span>{data.RecoGeneral8}</span>
                          </IonCol>
                        </IonRow>
                      }
                    </IonCol>
                  }

                  <IonCol size="12" size-sm="4">
                    {
                      data.RecoGeneral3 &&
                      <IonRow>
                        <IonCol>
                          <IonIcon icon={checkbox} color="primary"></IonIcon>
                          &nbsp;<span>{data.RecoGeneral3}</span>
                        </IonCol>
                      </IonRow>
                    }
                    {
                      data.RecoGeneral6 &&
                      <IonRow>
                        <IonCol>
                          <IonIcon icon={checkbox} color="primary"></IonIcon>
                          &nbsp;<span>{data.RecoGeneral6}</span>
                        </IonCol>
                      </IonRow>
                    }
                  </IonCol>
                  {data.RecoGeneral4 &&
                    <IonCol size="12" size-sm="4">
                      <IonRow>
                        <IonCol>
                          <IonIcon icon={checkbox} color="primary"></IonIcon>
                          &nbsp;<span>{data.RecoGeneral4}</span>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        {
                          data.RecoGeneral7 &&
                          <IonCol>
                            <IonIcon icon={checkbox} color="primary"></IonIcon>
                            &nbsp;<span>{data.RecoGeneral7}</span>
                          </IonCol>
                        }
                      </IonRow>
                    </IonCol>
                  }
                  <hr />
                </IonRow>
              }

              {
                data.Encabezamiento2 && <>
                  <IonRow>
                    <IonCol class="ion-no-padding">
                      <IonLabel className="recoHeader">
                        <b>{data.Encabezamiento2}</b>
                      </IonLabel>
                      {data.Encabezamiento &&
                        <p>{data.Encabezamiento}</p>
                      }
                    </IonCol>
                  </IonRow>
                  <IonRow class="ion-no-padding" className="recoContent">
                    <IonCol size="12" size-sm="6">
                      <ul>
                        {data.Mensaj1 ? data.Mensaj1.split('\n').map(
                          (value: string, index: number) => {
                            return (<li key={index}><p dangerouslySetInnerHTML={{ __html: value }}></p></li>);
                          }) : []}
                        {data.Mensaj3 ? data.Mensaj3.split('\n').map(
                          (value: string, index: number) => {
                            return (<li key={index}><p dangerouslySetInnerHTML={{ __html: value }}></p></li>);
                          }) : []}
                        {data.Mensaj5 ? data.Mensaj5.split('\n').map(
                          (value: string, index: number) => {
                            return (<li key={index}><p dangerouslySetInnerHTML={{ __html: value }}></p></li>);
                          }) : []}
                      </ul>
                    </IonCol>
                    <IonCol size="12" size-sm="6">
                      <ul>
                        {data.Mensaj2 ? data.Mensaj2.split('\n').map(
                          (value: string, index: number) => {
                            return (<li key={index}><p dangerouslySetInnerHTML={{ __html: value }}></p></li>);
                          }) : []}
                        {data.Mensaj4 ? data.Mensaj4.split('\n').map(
                          (value: string, index: number) => {
                            return (<li key={index}><p dangerouslySetInnerHTML={{ __html: value }}></p></li>);
                          }) : []}
                        {data.Mensaj6 ? data.Mensaj6.split('\n').map(
                          (value: string, index: number) => {
                            return (<li key={index}><p dangerouslySetInnerHTML={{ __html: value }}></p></li>);
                          }) : []}
                      </ul>
                    </IonCol>
                  </IonRow>
                </>
              }

              {
                data.PrecautionsTitle && <>
                  <IonRow>
                    <IonCol class="ion-no-padding">
                      <IonLabel className="recoHeader">
                        <b>{data.PrecautionsTitle}</b>
                      </IonLabel>
                    </IonCol>
                  </IonRow>
                  <IonRow class="ion-no-padding" className="recoContent">
                    <IonCol size="12" size-sm="6">
                      <ul>
                        {
                          data.Precautions.split('\n').map(
                            (value: string, index: number) => {
                              return (<li key={index}><p dangerouslySetInnerHTML={{ __html: value }}></p></li>);
                            })
                        }
                      </ul>
                    </IonCol>
                    <IonCol size="12" size-sm="6">
                      <ul>
                        {
                          data.Precautions2.split('\n').map(
                            (value: string, index: number) => {
                              return (<li key={index}><p dangerouslySetInnerHTML={{ __html: value }}></p></li>);
                            })
                        }
                      </ul>
                    </IonCol>
                  </IonRow>
                </>
              }

              {
                data.ExceptionsTitle && <>
                  <IonRow>
                    <IonCol class="ion-no-padding">
                      <IonLabel className="recoHeader">
                        <b>{data.ExceptionsTitle}</b>
                      </IonLabel>
                    </IonCol>
                  </IonRow>
                  <IonRow class="ion-no-padding" className="recoContent">
                    <IonCol size="12" size-sm="6">
                      <ul>
                        {
                          data.Exceptions.split('\n').map(
                            (value: string, index: number) => {
                              return (<li key={index}><p dangerouslySetInnerHTML={{ __html: value }}></p></li>);
                            })
                        }
                      </ul>
                    </IonCol>
                  </IonRow>
                </>
              }

              {
                data.Advantages && <>
                  <IonRow className="recoContent">
                    <IonCol size="6">
                      <IonLabel className="recoHeader">
                        <b>{t('Advantages')}</b>
                      </IonLabel>
                      <ul>
                        {data.Advantages.split('\n').map(
                          (value: string, index: number) => {
                            return (<li key={index.toString()}><p>{value}</p></li>);
                          })}
                      </ul>
                    </IonCol>
                    <IonCol size="6">
                      <IonLabel className="recoHeader">
                        <b>{t('Inconvenients')}</b>
                      </IonLabel>
                      <ul>
                        {data.Inconvenients.split('\n').map(
                          (value: string, index: number) => {
                            return (<li key={index.toString()}><p>{value}</p></li>);
                          })}
                      </ul>
                    </IonCol>
                  </IonRow>
                </>
              }
            </IonGrid>
            {
              showGenericRecommendation &&
              <table className="dosage_table">
                <tbody>
                  <tr>
                    <td>{t('Statin')}</td>
                    <td colSpan={2}>{t('Dosage')}</td>
                  </tr>
                  <tr>
                    <td></td>
                    <td>{t('Moderate-intensity')} <br />(LDL-C {t('Decreases')} 30-50%)</td>
                    <td>{t('High-intensity')} <br />(LDL-C {t('Decreases')} 50%)</td>
                  </tr>
                  <tr>
                    <td>{t('Rosuvastatin')}</td>
                    <td>10-20 mg/{t('Day')}</td>
                    <td>--</td>
                  </tr>
                  <tr>
                    <td>{t('Simvastatin')}</td>
                    <td>20-40 mg/{t('Day')}</td>
                    <td>--</td>
                  </tr>
                  <tr>
                    <td>{t('Atorvastatin')}</td>
                    <td>40-80 mg/{t('Day')}</td>
                    <td>40-80 mg/{t('Day')}</td>
                  </tr>
                  <tr>
                    <td>{t('Pravastatin')}</td>
                    <td>5-10 mg/{t('Day')}</td>
                    <td>20-40 mg/{t('Day')}</td>
                  </tr>
                  <tr>
                    <td>{t('Fluvastatin')}</td>
                    <td>40-80 mg/{t('Day')}</td>
                    <td>--</td>
                  </tr>
                </tbody>
              </table>
            }

            {
              !showGenericRecommendation &&
              <p dangerouslySetInnerHTML={{ __html: data && data[`DosageTable`] }} />}
          </div>
        </div>
      </IonContent>
    </IonPage >
  );
};

export default Recommendations;